import { graphql } from "gatsby"
import PropTypes from "prop-types"
import React from "react"
import CardGroup from "components/CardGroup"
import { useCustomersSectionData } from "effects/useCustomersContent"
import { getPageUrl } from "lib/functions"
import CaseStudy from "./CaseStudy"
import Customer from "./Customer"
import * as css from "./CustomersSection.module.css"
import Container from "components/ui/Container"
import Heading from "components/ui/Heading"
import Image from "components/ui/Image"

/**
 * Helper to get the URL for a case study.
 * Handles both blog post slugs and external URLs.
 */
function getCaseStudyUrl(blogPost) {
  const slug = blogPost?.slug || ""
  // Check if it's an external URL (e.g., Medium article)
  if (slug.startsWith("http://") || slug.startsWith("https://")) {
    return slug
  }
  // Otherwise use the standard blog URL format
  return getPageUrl(blogPost?.__typename || "ContentfulBlog", slug)
}

function CustomersSection({
  caseStudiesHeading: propCaseStudiesHeading,
  caseStudies: propCaseStudies,
  supersetCustomersHeading: propSupersetCustomersHeading,
  supersetCustomersImage: propSupersetCustomersImage,
  customersHeading: propCustomersHeading,
  customers: propCustomers,
  useLocalData = false,
}) {
  // Fetch local data if useLocalData is true or if no props provided
  const localData = useCustomersSectionData()

  // Use local data if explicitly requested or if Contentful props are empty
  const shouldUseLocal =
    useLocalData ||
    (!propCaseStudies?.length && !propCustomers?.length && localData)

  const caseStudiesHeading = shouldUseLocal
    ? localData.caseStudiesHeading
    : propCaseStudiesHeading
  const caseStudies = shouldUseLocal
    ? localData.caseStudies
    : propCaseStudies || []
  const supersetCustomersHeading = shouldUseLocal
    ? localData.supersetCustomersHeading
    : propSupersetCustomersHeading
  const supersetCustomersImage = shouldUseLocal
    ? localData.supersetCustomersImage
    : propSupersetCustomersImage
  const customersHeading = shouldUseLocal
    ? localData.customersHeading
    : propCustomersHeading
  const customers = shouldUseLocal ? localData.customers : propCustomers || []

  return (
    <>
      <Container section>
        {caseStudies.length > 0 && (
          <>
            <h4 className="text-base font-semibold text-gray">
              {caseStudiesHeading}
            </h4>
            <CardGroup
              className={css.cardGroup}
              itemsPerRow={3}
              spacing="2x"
              stackable
            >
              {caseStudies.map(({ id, thumbnail, description, blogPost }) => (
                <CaseStudy
                  key={id}
                  image={thumbnail}
                  text={description}
                  url={getCaseStudyUrl(blogPost)}
                />
              ))}
            </CardGroup>
          </>
        )}
      </Container>
      {supersetCustomersHeading && (
        <section className="my-10 overflow-hidden bg-big-stone pt-10 md:pt-12 lg:my-20 lg:pt-20">
          <Container className="relative z-10">
            <Heading title={supersetCustomersHeading} inverted />
          </Container>
          {supersetCustomersImage && (
            <div className="-mt-[15%] lg:container lg:-mt-40 lg:mr-0 lg:px-0 xl:-mt-44">
              <Image className="w-full" src={supersetCustomersImage} />
            </div>
          )}
        </section>
      )}
      <Container section>
        {customers.length > 0 && (
          <>
            <h4 className="text-base font-semibold text-lush">
              {customersHeading}
            </h4>
            <CardGroup
              className={css.cardGroup}
              itemsPerRow={4}
              spacing="1x"
              stackable
            >
              {customers.map(({ id, title, logo, website }) => (
                <Customer key={id} logo={logo} title={title} url={website} />
              ))}
            </CardGroup>
          </>
        )}
      </Container>
    </>
  )
}

CustomersSection.propTypes = {
  caseStudiesHeading: PropTypes.string,
  caseStudies: PropTypes.array,
  supersetCustomersHeading: PropTypes.string,
  supersetCustomersImage: PropTypes.object,
  customersHeading: PropTypes.string,
  customers: PropTypes.array,
  useLocalData: PropTypes.bool,
}

export default CustomersSection

export const query = graphql`
  fragment CustomersSection on ContentfulCustomersSection {
    id
    __typename
    caseStudiesHeading
    caseStudies {
      id
      description {
        raw
      }
      thumbnail {
        url
        title
      }
      blogPost {
        __typename
        slug
      }
    }
    supersetCustomersHeading
    supersetCustomersImage {
      gatsbyImageData(quality: 100, layout: FULL_WIDTH)
      description
    }
    customersHeading
    customers {
      id
      title
      logo {
        url
      }
      website
    }
  }
`
